import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import 'dayjs/locale/it';
import { RptAgendaCommercialeDto } from '@api/client';
import { ESITO_COLOR } from '../../_utils/esitoLabel';
import './styles.scss';

dayjs.locale('it');

interface Props {
  data: RptAgendaCommercialeDto[];
  onSelectEvent: (item: RptAgendaCommercialeDto) => void;
  daysAhead?: number;
}

const cap = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

const AgendaActionsUpcoming: React.FC<Props> = ({ data, onSelectEvent, daysAhead = 7 }) => {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState(false);

  const groups = useMemo(() => {
    const today = dayjs().startOf('day');
    const list: { key: string; label: string; items: RptAgendaCommercialeDto[] }[] = [];
    for (let i = 0; i < daysAhead; i += 1) {
      const d = today.add(i, 'day');
      const items = data
        .filter((a) => a.actionDate && dayjs(a.actionDate).isSame(d, 'day'))
        .sort((a, b) => new Date(a.actionDate!).getTime() - new Date(b.actionDate!).getTime());
      let label: string;
      if (i === 0) label = `${t('Oggi')} · ${cap(d.format('dddd'))} · ${d.format('D MMMM')}`;
      else if (i === 1) label = `${t('Domani')} · ${cap(d.format('dddd'))} · ${d.format('D MMMM')}`;
      else label = `${cap(d.format('dddd'))} · ${d.format('D MMMM')}`;
      list.push({ key: d.format('YYYY-MM-DD'), label, items });
    }
    return list;
  }, [data, t, daysAhead]);

  if (collapsed) {
    return (
      <button
        type="button"
        className="agenda-upcoming-collapsed"
        onClick={() => setCollapsed(false)}
        title={t('Mostra prossime attività')}
      >
        <i className="fas fa-chevron-left" />
        <span>{t('Prossime')}</span>
      </button>
    );
  }

  return (
    <div className="agenda-upcoming">
      <div className="agenda-upcoming__header">
        <span className="agenda-upcoming__title-text">
          <i className="far fa-calendar-alt me-2" />
          {t('Prossime attività')}
        </span>
        <button
          type="button"
          className="agenda-upcoming__toggle"
          onClick={() => setCollapsed(true)}
          title={t('Nascondi pannello')}
        >
          <i className="fas fa-chevron-right" />
        </button>
      </div>
      <div className="agenda-upcoming__body">
        {groups.map((g) => (
          <div key={g.key} className="agenda-upcoming__group">
            <div className={`agenda-upcoming__day-header${g.key === groups[0].key ? ' is-today' : ''}`}>
              {g.label}
            </div>
            {g.items.length === 0 ? (
              <div className="agenda-upcoming__empty">{t('Nessun evento programmato')}</div>
            ) : (
              g.items.map((item, j) => {
                const hasEsito = !!item.esito;
                const barColor = hasEsito ? (ESITO_COLOR[item.esito!] ?? '#BFBFBF') : '#BFBFBF';
                return (
                  <div
                    key={j}
                    className="agenda-upcoming__item"
                    role="button"
                    onClick={() => onSelectEvent(item)}
                  >
                    <span
                      className={`agenda-upcoming__bar${hasEsito ? '' : ' is-striped'}`}
                      style={{ backgroundColor: barColor }}
                    />
                    <div className="agenda-upcoming__content">
                      <div className="agenda-upcoming__item-title">
                        {item.nomeAccount || item.clienteId || '—'}
                      </div>
                      <div className="agenda-upcoming__item-sub">
                        {item.nominativoCliente}
                        {item.modalitaContatto && (
                          <>
                            {item.nominativoCliente ? ' · ' : ''}
                            {item.modalitaContatto}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default AgendaActionsUpcoming;
